/**
 * Which of the Overview's slice bars to draw (review of #177, 2026-10-09).
 *
 * The chart drew the 22 largest by labor. Once the no-claim payroll became its own bar
 * (#144), "Unknown (facility not resolved)" was left with ~$69K, fell out of the top
 * 22 for Facility group and Facility, and could not be found to check its drills.
 *
 * So the buckets that are not a real slice — the unresolved "Unknown" and the no-claim
 * payroll — are always drawn, under a rule at the end, outside the ranking. The ranked
 * bars are the largest `limit`, or all of them, plus the picked bar if it fell outside.
 */
import { UNKNOWN } from '@/data/blankValue'
import { isNoClaim } from '@/data/noClaim'

/** How many ranked bars the chart draws before "Show all". */
export const TOP_BARS = 22

/** Unknown (every blank spelling, as valueName folds it) or the no-claim payroll. */
export const isSpecialSlice = (key: string): boolean => key === UNKNOWN || isNoClaim(key)

export interface ShownBars<T> {
  /** Real slices, largest first, then the picked one if the cutoff dropped it. */
  ranked: T[]
  /** Unknown and no-claim, when the breakdown has them: drawn below the rule. */
  pinned: T[]
  /** Real slices in the breakdown — what "Show all N" counts. */
  rankedTotal: number
  /** Whether the cutoff hides any real slice, i.e. whether the toggle means anything. */
  cut: boolean
}

/**
 * `items` sorted largest first. Pinned bars keep that order among themselves; the
 * picked bar is never duplicated.
 */
export function shownBars<T extends { key: string }>(
  items: readonly T[],
  { selected, showAll, limit = TOP_BARS }: { selected: string | null; showAll: boolean; limit?: number },
): ShownBars<T> {
  const real = items.filter((i) => !isSpecialSlice(i.key))
  const pinned = items.filter((i) => isSpecialSlice(i.key))
  const ranked = showAll ? [...real] : real.slice(0, limit)
  if (selected && !ranked.some((i) => i.key === selected)) {
    const extra = real.find((i) => i.key === selected)
    if (extra) ranked.push(extra)
  }
  return { ranked, pinned, rankedTotal: real.length, cut: real.length > limit }
}

/**
 * The slice chart's column sort (review #402, Shayna 2026-10-12: "need to be able to
 * sort both lines largest to smallest and vice versa"). Null is the default ranking,
 * largest payroll labor first. A column head cycles largest first → smallest first →
 * back to the default.
 */
export type BarSortKey = 'claims' | 'cpc'
export type BarSort = { key: BarSortKey; dir: 'asc' | 'desc' } | null

export const BAR_SORT_KEYS: readonly BarSortKey[] = ['claims', 'cpc']

/** Only a column the chart has; anything else in a hand-edited URL reads as the default. */
export const barSortOf = (s: { key: string; dir: 'asc' | 'desc' } | null): BarSort =>
  s && (BAR_SORT_KEYS as readonly string[]).includes(s.key) ? { key: s.key as BarSortKey, dir: s.dir } : null

/** The next sort when a column head is clicked. */
export function nextBarSort(cur: BarSort, key: BarSortKey): BarSort {
  if (!cur || cur.key !== key) return { key, dir: 'desc' }
  return cur.dir === 'desc' ? { key, dir: 'asc' } : null
}

/**
 * `items` (already largest labor first) reordered by `sort`. A slice with no rate —
 * labor but no claims — sorts last either way: "—" is neither high nor low. Ties keep
 * the labor order, so the result is stable. Unknown and no-claim are pinned by
 * shownBars whatever this does.
 */
export function sortBars<T extends { claims: number; cpc: number | null }>(
  items: readonly T[],
  sort: BarSort,
): T[] {
  if (!sort) return [...items]
  const sign = sort.dir === 'desc' ? -1 : 1
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      const x = sort.key === 'claims' ? a.item.claims : a.item.cpc
      const y = sort.key === 'claims' ? b.item.claims : b.item.cpc
      if (x === null || y === null) return x === y ? a.i - b.i : x === null ? 1 : -1
      return x === y ? a.i - b.i : sign * (x - y)
    })
    .map((e) => e.item)
}

/** How the caption names the order the bars are in. */
export function barSortPhrase(sort: BarSort): string {
  if (!sort) return 'by payroll labor'
  const col = sort.key === 'claims' ? 'billed claims' : 'cost per claim'
  return `by ${col}, ${sort.dir === 'desc' ? 'highest' : 'lowest'} first`
}
